import React, { ForwardedRef, forwardRef, useEffect, useRef, useState } from "react"
import GUI, { IGUIProps } from "../GUI"
import UserPref from "../UserPref"

export type TVResizerProps = IGUIProps & {
    position?:"start"|"end"
    type?:"vertical"|"horizontal"
    min:number
    max:number
    threshold?:number           // minimum threshold
    prefKey?:string
    children:React.ReactNode
    onResize?:(size:number) => void
}

function constraint(min:number, max:number, value:number) {
    return Math.max(Math.min(value, max), min)
}

export interface IResizer {

}
export default forwardRef((props:TVResizerProps, ref:ForwardedRef<IResizer>) => {
    const [resizing,setResizing] = useState<boolean>(false)
    const [snapping,setSnapping] = useState<boolean>(false)

    const self = useRef<HTMLDivElement|null>(null)
    const w = useRef<number>(0)
    const x = useRef<number>(0)

    const type = props.type || "vertical"
    const vert = type=="vertical"

    const position = props.position || "end"
    const mirror = position == "start" ? -1 : 1

    const getSize = () => {
        if (!self.current) return 0
        const rect = self.current.getBoundingClientRect()
        return vert ? rect.width : rect.height
    }
    const setSize = (size:number) => {
        if (!size) return
        self.current?.style.setProperty(type=="vertical" ? "width" : "height", size+"px")
        props.onResize?.(size)
    }
    const onDown = (e:React.PointerEvent) => {
        if (snapping || !self.current) return
        e.preventDefault()
        
        x.current = vert ? e.clientX : e.clientY
        w.current = getSize()
        setResizing(true)
        document.addEventListener("pointerup", onUp)
        document.addEventListener("pointermove", onMove)
    }
    const onMove = (e:PointerEvent) => {
        const size = type=="vertical" ? w.current + e.clientX - x.current : w.current + e.clientY - x.current
        setSize(constraint(props.min, props.max, size*mirror))
    }
    const snapTo = async (value:number) => {
        if (!self.current) return
        setSnapping(true)
        const transition = self.current.style.getPropertyValue("transition")
        self.current.style.setProperty("transition", "all 0.125s ease-in-out")
        await GUI.tick()
        if (!self.current) return
        setSize(constraint(props.min, props.max, props.min))
        await GUI.transitionEnd(self.current, undefined, 125)
        if (transition) self.current.style.setProperty("transition", transition)
        else self.current.style.removeProperty("transition")
        setSnapping(false)
    }
    const onUp = () => {
        document.removeEventListener("pointerup", onUp)
        document.removeEventListener("pointermove", onMove)
        setResizing(false)
        if (props.threshold && getSize()<props.threshold) snapTo(props.threshold)
    }

    useEffect(() => {
        self.current && props.prefKey && setSize(UserPref.i.get(props.prefKey))
    }, [])

    useEffect(() => {
        setSize(constraint(props.min, props.max, getSize()))
    }, [props.min, props.max])

    return <div className={GUI.joinClass("resizer", props.className, type, position, resizing ? "resizing" : "", snapping ? "snapping" : "")} ref={self}>
        {props.children}
        <div className={GUI.joinClass("divider", position)} onPointerDown={onDown}></div>
    </div>
})